/**
 * A list of import or forecast notes that does not swamp the page.
 *
 * These are written by the AI and there can be a lot of them: Computacenter's
 * import produced thirty, which pushed the headline figures, the balance
 * diagnosis and the valuation below the fold. A wall of amber that has to be
 * scrolled past is read once and then ignored, which defeats the point of
 * writing it.
 *
 * The first few always show, because the panel earns its place by being seen
 * before the user starts trusting the figures. The rest collapse behind a
 * count, so the page says how much more there is without spending the height.
 */

import { useState } from 'react';

const ALWAYS_VISIBLE = 3;

export function CollapsibleNotes({
  title,
  notes,
}: {
  title: string;
  notes: string[];
}) {
  const [expanded, setExpanded] = useState(false);

  if (notes.length === 0) return null;

  const hidden = notes.length - ALWAYS_VISIBLE;
  const shown = expanded ? notes : notes.slice(0, ALWAYS_VISIBLE);

  return (
    <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="font-medium">
        {title}
        {notes.length > 1 && (
          <span className="ml-1 font-normal text-amber-700">
            ({notes.length})
          </span>
        )}
      </div>
      <ul className="mt-1 list-disc space-y-0.5 pl-4">
        {shown.map((note, index) => (
          <li key={`${index}-${note}`}>{note}</li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="mt-2 text-sm font-medium text-amber-900 underline underline-offset-2 hover:text-amber-950"
          aria-expanded={expanded}
        >
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}
